import GET_WAREHOUSE_RELEASE_COMPONENT_VARIANTS from 'api/queries/GET_WAREHOUSE_RELEASE_COMPONENT_VARIANTS';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import GET_MODEL_SPECIFICATION_VARIANTS from 'api/queries/GET_MODEL_SPECIFICATION_VARIANTS';
import SmartTable from 'components/common/SmartTable';
import ThemedSwitch from 'components/common/ThemedSwitch';
import Matrix from 'utils/Matrix';
import useDebounceMatrix from 'components/common/hooks/useDebounceMatrix';
import GET_WAREHOUSE_RELEASE_MODEL_VARIANTS from 'api/queries/GET_WAREHOUSE_RELEASE_MODEL_VARIANTS';
import GET_WAREHOUSE_RELEASE from 'api/queries/GET_WAREHOUSE_RELEASE';
import SET_WAREHOUSE_RELEASE_MODEL_TO_NOT_READY from 'api/mutations/SET_WAREHOUSE_RELEASE_MODEL_TO_NOT_READY';
import SET_WAREHOUSE_RELEASE_MODEL_TO_READY from 'api/mutations/SET_WAREHOUSE_RELEASE_TO_READY';
import parseWarehouseReleaseModelVariants from 'parsers/specificationProductionDetails/parseWarehouseReleaseModelVariants';
import QuantityCell from 'views/WarehouseReleasesList/QuantityCell';
import UPDATE_WAREHOUSE_RELEASE_MODEL_VARIANT from 'api/mutations/UPDATE_WAREHOUSE_RELEASE_MODEL_VARIANT';
import { WarehouseReleaseContext } from '../WarehouseReleaseProvider';
import './WarehouseReleaseModelVolume.scss';
import { dataValidationErrorHandler } from '../../../errors/dataValidationError';
const WarehouseReleaseModelVolume = () => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { t } = useTranslation('warehouseReleases');
    const { warehouseRelease } = useContext(WarehouseReleaseContext);
    const [updatePending, setUpdatePending] = useState(false);
    const { data: warehouseReleaseVolumeData } = useQuery(GET_WAREHOUSE_RELEASE_MODEL_VARIANTS, {
        variables: { id: (_a = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _a === void 0 ? void 0 : _a.id },
    });
    const models = (warehouseReleaseVolumeData === null || warehouseReleaseVolumeData === void 0 ? void 0 : warehouseReleaseVolumeData.node) || undefined;
    const [updateWarehouseReleaseModelVariant] = useMutation(UPDATE_WAREHOUSE_RELEASE_MODEL_VARIANT, {
        refetchQueries: [
            {
                query: GET_WAREHOUSE_RELEASE_MODEL_VARIANTS,
                variables: { id: (_b = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _b === void 0 ? void 0 : _b.id },
            },
            'GetWarehouseReleases',
        ],
    });
    const statusMutationOptions = {
        refetchQueries: [
            {
                query: GET_WAREHOUSE_RELEASE_MODEL_VARIANTS,
                variables: { id: (_c = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _c === void 0 ? void 0 : _c.id },
            },
            {
                query: GET_MODEL_SPECIFICATION_VARIANTS,
                variables: {
                    modelSpecificationId: (_e = (_d = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _d === void 0 ? void 0 : _d.modelSpecification) === null || _e === void 0 ? void 0 : _e.id,
                },
            },
            'GetWarehouseReleases',
            { query: GET_WAREHOUSE_RELEASE, variables: { id: warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.id } },
        ],
    };
    const [setWarehouseReleaseModelToReady] = useMutation(SET_WAREHOUSE_RELEASE_MODEL_TO_READY, statusMutationOptions);
    const [setWarehouseReleaseModelToNotReady] = useMutation(SET_WAREHOUSE_RELEASE_MODEL_TO_NOT_READY, statusMutationOptions);
    const warehouseReleaseModelVariants = useMemo(() => parseWarehouseReleaseModelVariants(models), [models]);
    const warehouseReleaseMatrix = useMemo(() => Matrix.create({
        w: warehouseReleaseModelVariants.variants.length,
        object: warehouseReleaseModelVariants.variants,
        mapRow: (row) => row === null || row === void 0 ? void 0 : row.quantities,
        mapCell: (variant) => {
            var _a;
            return ({
                id: variant === null || variant === void 0 ? void 0 : variant.id,
                value: variant === null || variant === void 0 ? void 0 : variant.quantity,
                expectedValue: (_a = variant === null || variant === void 0 ? void 0 : variant.correspondingSpecificationVariant) === null || _a === void 0 ? void 0 : _a.quantity,
            });
        },
        h: undefined,
        fill: undefined,
        data: undefined,
        rows: undefined,
    }), [warehouseReleaseModelVariants]);
    const handleQuantitiesChange = useCallback((values) => {
        const variantsQuantity = values.map((e) => e.val);
        updateWarehouseReleaseModelVariant({
            variables: { variantsQuantity },
        }).catch(dataValidationErrorHandler());
    }, [updateWarehouseReleaseModelVariant]);
    const [update] = useDebounceMatrix(handleQuantitiesChange);
    const quantityChange = useCallback((id, value) => {
        update([id, value]);
        setUpdatePending(false);
    }, [update]);
    const [ready, setReady] = useState(true);
    useEffect(() => {
        var _a;
        setReady((_a = models === null || models === void 0 ? void 0 : models.ready) !== null && _a !== void 0 ? _a : true);
    }, [models === null || models === void 0 ? void 0 : models.ready, setReady, warehouseReleaseVolumeData]);
    const isDraft = (warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.status) === 'DRAFT';
    const isSetToReadyAvailable = models === null || models === void 0 ? void 0 : models.isSetToReadyAvailable;
    const editDisabled = !isDraft || ready;
    const handleReady = useCallback(() => {
        var _a, _b;
        return setWarehouseReleaseModelToReady({
            variables: {
                warehouseReleaseModelId: (_a = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _a === void 0 ? void 0 : _a.id,
            },
            refetchQueries: [
                {
                    query: GET_WAREHOUSE_RELEASE_COMPONENT_VARIANTS,
                    variables: {
                        id: warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.id,
                    },
                },
                {
                    query: GET_WAREHOUSE_RELEASE_MODEL_VARIANTS,
                    variables: { id: (_b = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _b === void 0 ? void 0 : _b.id },
                },
            ],
        });
    }, [
        setWarehouseReleaseModelToReady,
        warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.id,
        (_f = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _f === void 0 ? void 0 : _f.id,
    ]);
    const doSetNotReady = useCallback(() => {
        var _a, _b;
        setWarehouseReleaseModelToNotReady({
            variables: {
                warehouseReleaseModelId: (_a = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _a === void 0 ? void 0 : _a.id,
            },
            refetchQueries: [
                {
                    query: GET_WAREHOUSE_RELEASE_COMPONENT_VARIANTS,
                    variables: {
                        id: warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.id,
                    },
                },
                {
                    query: GET_WAREHOUSE_RELEASE_MODEL_VARIANTS,
                    variables: { id: (_b = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _b === void 0 ? void 0 : _b.id },
                },
            ],
        });
    }, [
        setWarehouseReleaseModelToNotReady,
        warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.id,
        (_g = warehouseRelease === null || warehouseRelease === void 0 ? void 0 : warehouseRelease.warehouseReleaseModel) === null || _g === void 0 ? void 0 : _g.id,
    ]);
    const toggleReady = useCallback(() => {
        setReady(!ready);
        if (ready)
            doSetNotReady();
        else
            handleReady();
    }, [ready, doSetNotReady, handleReady]);
    return (React.createElement("div", { className: "WarehouseReleaseModelVolume" },
        React.createElement("div", { className: "ModelVolume" },
            React.createElement("h4", { className: "mt-0" }, t('warehouse_release_volume_title')),
            React.createElement(SmartTable, { head: [
                    t('common:version'),
                    ...warehouseReleaseModelVariants.sizes.map((size) => { var _a; return ((_a = size === null || size === void 0 ? void 0 : size.sizeName) === null || _a === void 0 ? void 0 : _a.name) || ''; }),
                ], matrix: warehouseReleaseMatrix === null || warehouseReleaseMatrix === void 0 ? void 0 : warehouseReleaseMatrix.data, renderBeginColumns: (_row, index) => {
                    const variant = warehouseReleaseMatrix === null || warehouseReleaseMatrix === void 0 ? void 0 : warehouseReleaseMatrix.object[index];
                    return [React.createElement("span", null, variant.colorName || '')];
                }, renderCell: (value) => (React.createElement(QuantityCell, { cellValue: value, onChange: quantityChange, disabled: editDisabled || (value === null || value === void 0 ? void 0 : value.expectedValue) === undefined, precision: 0, updateStartHook: () => setUpdatePending(true) })) })),
        isDraft && (React.createElement("div", { className: "OrderModelReady" },
            React.createElement("div", { className: "flex justify-end" },
                React.createElement("span", { className: "ready mr-4" }, t('warehouse_release_model_ready')),
                React.createElement(ThemedSwitch, { onChange: toggleReady, checked: ready, disabled: updatePending || (!ready && !isSetToReadyAvailable) }))))));
};
export default WarehouseReleaseModelVolume;
